import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { VoiceboxChatPage } from 'vet-bones/components/molecules/Voicebox';
import { isEqual } from 'vet-bones/utils';
import { useDidValueChange, useIsMounted } from 'vet-bones/utils/hooks';

import { Loading } from 'src/ui/components/Loading';
import { VOICEBOX_PAGE_DRAWER_BODY_CONTAINER } from 'src/ui/constants/testIds';
import { useVoiceboxConversations } from 'src/ui/containers/voicebox/aside/useVoiceboxConversations';
import { useVoiceboxContext } from 'src/ui/containers/voicebox/VoiceboxContext';
import { useProfile, useSetLastConnectionIndex } from 'src/ui/hooks/connection';
import { useVoiceboxRouteParams } from 'src/ui/hooks/navbar/routes';
import { useQueryParams } from 'src/ui/hooks/query';
import {
  DEFAULT_VOICEBOX_NAMED_GRAPH,
  useNotAllowedVoiceboxPage,
} from 'src/ui/hooks/voicebox';
import { buildRoute } from 'src/ui/utils/buildRoute';

export const VoiceboxPage: React.VFC = () => {
  const isMounted = useIsMounted();
  const history = useHistory();
  const queryParams = useQueryParams();

  const params = useVoiceboxRouteParams();
  const conversationId = params?.conversation || '';
  const connectionIdx = Number(params?.id) || 0;

  const { data: profileData } = useProfile();
  const voiceboxContext = useVoiceboxContext();

  const { setInitialSettings } = voiceboxContext?.pageData || {};
  React.useEffect(() => {
    const databaseId = queryParams.get('db') || '';
    const graphs = queryParams.get('graph') || '';
    const model = queryParams.get('model') || '';

    if (databaseId || graphs || model) {
      setInitialSettings?.((prev) => {
        const next = {
          databaseId,
          model,
          namedGraphs: graphs
            ? graphs.split(',')
            : [DEFAULT_VOICEBOX_NAMED_GRAPH],
          reasoning: true,
        };
        return isEqual(prev, next) ? prev : next;
      });
    }
  }, [history, queryParams, setInitialSettings]);

  const { loading: isLoadingPage, notAllowed } = useNotAllowedVoiceboxPage(
    params?.id || '',
    params?.org || ''
  );
  const didIsLoadingPageChange = useDidValueChange(isLoadingPage);
  const needsRedirect =
    (voiceboxContext &&
      !voiceboxContext.isLoadingConnection &&
      !voiceboxContext.connection) ||
    (didIsLoadingPageChange && !isLoadingPage && notAllowed);

  React.useEffect(() => {
    if (needsRedirect) {
      const path = buildRoute(params?.org, connectionIdx);
      console.warn(`notAllowed true, redirecting to ${path}`);
      history.push(path);
    }
  }, [connectionIdx, history, needsRedirect, params]);

  const conversations = useVoiceboxConversations(
    voiceboxContext?.connection?.id || ''
  );
  const didConversationIdChange = useDidValueChange(conversationId);
  React.useEffect(() => {
    if (
      didConversationIdChange &&
      conversationId &&
      profileData?.profile &&
      !profileData.profile.is_static_voicebox
    ) {
      (async () => {
        const items = await conversations.refetch({ cancelRefetch: true });
        if (!isMounted.current) {
          return;
        }
        if (
          items.data?.listVoiceboxConversations &&
          !items.data.listVoiceboxConversations.find(
            (item) => item?.id === conversationId
          )
        ) {
          history.push(buildRoute(params?.org, connectionIdx, 'voicebox/'));
        }
      })();
    }
  }, [
    connectionIdx,
    conversationId,
    conversations,
    didConversationIdChange,
    history,
    isMounted,
    params,
    profileData,
  ]);

  // set current page as last visited connection index
  useSetLastConnectionIndex(
    voiceboxContext?.connection?.index,
    params?.org ?? '',
    !notAllowed
  );

  if (
    notAllowed ||
    !voiceboxContext ||
    !voiceboxContext.connection ||
    !voiceboxContext.pageData
  ) {
    return null;
  }

  const body = isLoadingPage ? (
    <Loading />
  ) : (
    <div
      className="sd-voicebox-container"
      data-testid={VOICEBOX_PAGE_DRAWER_BODY_CONTAINER}
    >
      <VoiceboxChatPage />
    </div>
  );

  return (
    <div className="sd-voicebox-page">
      <div className="sd-voicebox-page-body">{body}</div>
    </div>
  );
};
